import { PropsWithChildren, useMemo } from "react";

import { useCurrentPlatformContext } from "@/contexts/CurrentPlatformContext";
import { useGetUserPlatformRoleQuery, useGetUserRolesQuery } from "@/graphql";
import { mapToUserPlatformRole } from "@/mappers";

import { UserRolesContext } from "./UserRolesContext";

export const UserRolesProvider = ({ children }: PropsWithChildren) => {
  const { platform: currentPlatform } = useCurrentPlatformContext();
  const platformPublicId = currentPlatform?.id;

  const {
    data: userPlatformRoleData,
    loading: userPlatformRoleLoading,
    error: userPlatformRoleError,
  } = useGetUserPlatformRoleQuery({
    variables: {
      platformPublicId,
    },
    skip: !platformPublicId,
  });

  const {
    data: userRolesData,
    loading: userRolesLoading,
    error: userRolesError,
  } = useGetUserRolesQuery({
    skip: !platformPublicId,
  });

  const platformRole = useMemo(() => {
    return userPlatformRoleData?.userRole
      ? mapToUserPlatformRole(userPlatformRoleData?.userRole)
      : null;
  }, [userPlatformRoleData?.userRole]);

  const roles = useMemo(() => {
    return userRolesData?.userRoles ?? [];
  }, [userRolesData?.userRoles]);

  const contextValue = useMemo(
    () => ({
      platformRole,
      roles,
      loading: userPlatformRoleLoading || userRolesLoading,
      isError: !!userPlatformRoleError || !!userRolesError,
    }),
    [
      platformRole,
      roles,
      userPlatformRoleLoading,
      userRolesLoading,
      userPlatformRoleError,
      userRolesError,
    ]
  );

  return <UserRolesContext.Provider value={contextValue}>{children}</UserRolesContext.Provider>;
};
